import { useState, useEffect, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ItemType, MaterialGroup, StockItem } from '../../types';
import { createStockItem, CreateStockItemRequest, fetchStockItems } from '../../services/storage';
import { Package, Ruler, Weight, MapPin, DollarSign, Tag, Barcode, Link2, Copy, X, Camera } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useToast } from '../../contexts/ToastContext';
import { BarcodeScannerDialog } from './BarcodeScannerDialog';

const ITEM_TYPES: { value: ItemType; label: string }[] = [
  { value: 'MATERIA_PRIMA', label: 'Matéria-prima' },
  { value: 'FERRAMENTA', label: 'Ferramenta' },
  { value: 'PRODUTO_ACABADO', label: 'Produto Acabado' },
  { value: 'RETALHO', label: 'Retalho' },
  { value: 'EMBALAGEM', label: 'Embalagem' },
];

interface CreateItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  groups: MaterialGroup[];
  onSuccess: () => void;
  /** Pre-select item type when opening (e.g., RETALHO from "Gerar Retalho" tab) */
  defaultItemType?: ItemType;
}

export function CreateItemDialog({
  open,
  onOpenChange,
  accessToken,
  groups,
  onSuccess,
  defaultItemType,
}: CreateItemDialogProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [itemType, setItemType] = useState<ItemType>(defaultItemType || 'MATERIA_PRIMA');
  const [skuCode, setSkuCode] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [thickness, setThickness] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [minStock, setMinStock] = useState('');
  const [unit, setUnit] = useState('un');
  const [location, setLocation] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [barcode, setBarcode] = useState('');
  const [tags, setTags] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);

  // Parent item (for RETALHO linking)
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState<StockItem[]>([]);
  const [selectedParent, setSelectedParent] = useState<StockItem | null>(null);
  const [searchingParent, setSearchingParent] = useState(false);

  // Sync defaultItemType when it changes (e.g., opening from "Gerar Retalho" tab)
  useEffect(() => {
    if (defaultItemType && open) {
      setItemType(defaultItemType);
    }
  }, [defaultItemType, open]);

  // Debounced parent search
  useEffect(() => {
    if (itemType !== 'RETALHO' || !parentSearch.trim() || selectedParent) return;

    const timer = setTimeout(async () => {
      setSearchingParent(true);
      try {
        const response = await fetchStockItems(accessToken, {
          search: parentSearch.trim(),
          size: 10,
        });
        // Show items that can be a source (typically MATERIA_PRIMA or even other RETALHO)
        setParentResults(response.items || []);
      } catch {
        setParentResults([]);
      } finally {
        setSearchingParent(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [parentSearch, itemType, accessToken, selectedParent]);

  const handleSelectParent = (item: StockItem) => {
    setSelectedParent(item);
    setParentSearch('');
    setParentResults([]);
  };

  const handleClearParent = () => {
    setSelectedParent(null);
    setParentSearch('');
    setParentResults([]);
  };

  const handleInheritFromParent = () => {
    if (!selectedParent) return;
    if (selectedParent.thickness) setThickness(String(selectedParent.thickness));
    if (selectedParent.location) setLocation(selectedParent.location);
    if (selectedParent.weightKg) setWeightKg(String(selectedParent.weightKg));
    if (selectedParent.costPerUnit) setCostPerUnit(String(selectedParent.costPerUnit));
    toast.success('Propriedades herdadas do material de origem');
  };

  const resetForm = () => {
    setName('');
    setItemType(defaultItemType || 'MATERIA_PRIMA');
    setSkuCode('');
    setWidth('');
    setHeight('');
    setThickness('');
    setWeightKg('');
    setQuantity('1');
    setMinStock('');
    setUnit('un');
    setLocation('');
    setCostPerUnit('');
    setBatchNumber('');
    setPurchaseDate('');
    setExpirationDate('');
    setBarcode('');
    setTags('');
    setSelectedParent(null);
    setParentSearch('');
    setParentResults([]);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    try {
      const request: CreateStockItemRequest = {
        name: name.trim(),
        itemType,
        skuCode: skuCode.trim() || undefined,
        width: width ? parseFloat(width) : undefined,
        height: height ? parseFloat(height) : undefined,
        thickness: thickness ? parseFloat(thickness) : undefined,
        weightKg: weightKg ? parseFloat(weightKg) : undefined,
        quantity: quantity ? parseInt(quantity) : 1,
        minStock: minStock ? parseInt(minStock) : undefined,
        unit: unit.trim() || 'un',
        location: location.trim() || undefined,
        costPerUnit: costPerUnit ? parseFloat(costPerUnit) : undefined,
        parentItemId: selectedParent?.id || undefined,
        batchNumber: batchNumber.trim() || undefined,
        purchaseDate: purchaseDate || undefined,
        expirationDate: expirationDate || undefined,
        barcodes: barcode.trim() ? [barcode.trim()] : undefined,
        tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
      };

      await createStockItem(accessToken, request);
      toast.success(itemType === 'RETALHO' ? 'Retalho criado com sucesso!' : 'Item criado com sucesso!');
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const isRetalho = itemType === 'RETALHO';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Novo Item de Estoque
          </DialogTitle>
          <DialogDescription>
            Cadastre um novo item no sistema de estoque.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground">Informações Básicas</h3>

            {/* Mobile: Name full width, then Type+SKU in a row. Desktop: single row */}
            <div className="space-y-3 md:space-y-0 md:grid md:gap-3 md:grid-cols-[1fr_auto_auto]">
              <div className="space-y-1.5">
                <Label htmlFor="name">Nome do Item *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Chapa de Acrílico Cristal"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3 md:contents">
                <div className="space-y-1.5 md:w-44">
                  <Label htmlFor="itemType">Tipo *</Label>
                  <Select value={itemType} onValueChange={(v) => setItemType(v as ItemType)}>
                    <SelectTrigger id="itemType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5 md:w-48">
                  <Label htmlFor="skuCode">Código SKU</Label>
                  <Input
                    id="skuCode"
                    value={skuCode}
                    onChange={(e) => setSkuCode(e.target.value)}
                    placeholder="Auto-gerado"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Parent Item (only for RETALHO) */}
          {isRetalho && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Material de Origem (opcional)
              </h3>

              {selectedParent ? (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="font-mono text-xs text-muted-foreground">{selectedParent.skuCode}</div>
                      <div className="font-medium text-sm">{selectedParent.name}</div>
                      {selectedParent.width && selectedParent.height && (
                        <div className="text-xs text-muted-foreground">
                          {selectedParent.width} x {selectedParent.height}
                          {selectedParent.thickness && ` x ${selectedParent.thickness}`} mm
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearParent}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="Buscar material de origem por nome ou SKU..."
                    value={parentSearch}
                    onChange={(e) => setParentSearch(e.target.value)}
                  />
                  {searchingParent && (
                    <div className="text-xs text-muted-foreground mt-1">Buscando...</div>
                  )}
                  {parentResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                      {parentResults.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={cn(
                            "w-full text-left px-3 py-2 hover:bg-muted/50 border-b last:border-b-0",
                            "flex items-center justify-between"
                          )}
                          onClick={() => handleSelectParent(item)}
                        >
                          <div>
                            <div className="font-medium text-sm">{item.name}</div>
                            <div className="font-mono text-xs text-muted-foreground">{item.skuCode}</div>
                          </div>
                          {item.width && item.height && (
                            <span className="text-xs text-muted-foreground">
                              {item.width}x{item.height}
                              {item.thickness && `x${item.thickness}`}mm
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleInheritFromParent}
                disabled={!selectedParent}
                className="gap-1"
              >
                <Copy className="h-3 w-3" />
                Herdar propriedades
              </Button>

              <p className="text-xs text-muted-foreground">
                Vincule a um material de origem, ou deixe em branco para cadastrar avulso.
              </p>
            </div>
          )}

          {/* Dimensions + Quantity in one block */}
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <Ruler className="h-4 w-4" />
                Dimensões (mm) e Peso
              </h3>
              <div className="grid gap-3 grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="width">Largura</Label>
                  <Input id="width" type="number" step="0.01" min="0" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="height">Altura</Label>
                  <Input id="height" type="number" step="0.01" min="0" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="thickness">Espessura</Label>
                  <Input id="thickness" type="number" step="0.01" min="0" value={thickness} onChange={(e) => setThickness(e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="weightKg" className="flex items-center gap-1">
                    <Weight className="h-3 w-3" />
                    Peso (kg)
                  </Label>
                  <Input id="weightKg" type="number" step="0.001" min="0" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="0" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground">Quantidade e Estoque</h3>
              <div className="grid gap-3 grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="quantity">Quantidade *</Label>
                  <Input id="quantity" type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="unit">Unidade</Label>
                  <Input id="unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="un" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="minStock">Estoque Mínimo</Label>
                  <Input id="minStock" type="number" min="0" value={minStock} onChange={(e) => setMinStock(e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="costPerUnit" className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Custo Unitário
                  </Label>
                  <Input id="costPerUnit" type="number" step="0.01" min="0" value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* Location & Tracking + Barcode & Tags side by side */}
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Localização e Rastreio
              </h3>
              <div className="grid gap-3 grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="location">Localização</Label>
                  <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex: Prateleira A-01" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="batchNumber">Número do Lote</Label>
                  <Input id="batchNumber" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="Ex: LOTE-2026-001" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="purchaseDate">Data de Compra</Label>
                  <Input id="purchaseDate" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="expirationDate">Data de Validade</Label>
                  <Input id="expirationDate" type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <Barcode className="h-4 w-4" />
                Código de Barras e Tags
              </h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="barcode">Código de Barras Externo</Label>
                  <div className="flex gap-2">
                    <Input id="barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Escaneie ou digite o código" className="flex-1" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setScannerOpen(true)}
                      title="Escanear com câmera"
                      className="shrink-0"
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Código do fornecedor ou existente</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tags" className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    Tags
                  </Label>
                  <Input id="tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Ex: urgente, cliente-x, projeto-y" />
                  <p className="text-xs text-muted-foreground">Separe as tags por vírgula</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Criar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={(code) => {
          setBarcode(code);
          toast.success(`Código escaneado: ${code}`);
        }}
      />
    </Dialog>
  );
}
